import i18n from 'i18next';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { getSupportedLanguageCodes } from './languages';

// Language direction mapping
export const languageDirections: Record<string, 'ltr' | 'rtl'> = {
  en: 'ltr',
  es: 'ltr',
  fr: 'ltr',
  de: 'ltr',
  ar: 'rtl',
  he: 'rtl',
  fa: 'rtl',
};

// Function to get direction for a language
export const getLanguageDirection = (language: string): 'ltr' | 'rtl' => {
  return languageDirections[language] || 'ltr';
};

// Function to update document direction
export const updateDocumentDirection = (language: string) => {
  const direction = getLanguageDirection(language);
  document.documentElement.dir = direction;
  document.documentElement.lang = language;

  // Add direction class to body for CSS targeting
  document.body.classList.remove('ltr', 'rtl');
  document.body.classList.add(direction);

  return direction;
};

const i18nConfig = {
  // Language detection options
  detection: {
    order: ['localStorage', 'navigator', 'htmlTag'],
    caches: ['localStorage'],
    lookupLocalStorage: 'i18nextLng',
    checkWhitelist: true,
  },

  // Backend options for loading translation files
  backend: {
    loadPath: '/locales/{{lng}}/{{ns}}.json',
  },

  // Fallback language
  fallbackLng: 'en',

  // Available languages (including RTL languages)
  supportedLngs: getSupportedLanguageCodes(),

  // Debug mode (disable in production)
  debug: process.env.NODE_ENV === 'development',

  // Namespace and default namespace
  defaultNS: 'translation',
  ns: ['translation'],

  // Interpolation options
  interpolation: {
    escapeValue: false, // React already escapes values
  },

  // React specific options
  react: {
    useSuspense: false, // Set to false to avoid suspense issues
    bindI18n: 'languageChanged',
    bindI18nStore: 'added removed',
  },

  // Ensure resources are loaded before rendering
  initImmediate: false,

  // Resources loading
  load: 'languageOnly' as const, // Load only 'en' instead of 'en-US'

  // Key separator and namespace separator
  keySeparator: '.',
  nsSeparator: ':',
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(i18nConfig);

// Handle direction changes when language changes
i18n.on('languageChanged', (language: string) => {
  updateDocumentDirection(language);
});

// Set initial direction
updateDocumentDirection(i18n.language);

// Add direction helper to i18n instance
i18n.dir = (language?: string) => {
  return getLanguageDirection(language || i18n.language);
};

export default i18n; 